import { useEffect, useMemo, useState } from "react";
import { EducationalOrganizationsOptimizedDTO, MediaDTO, ProfessionDTO } from "../../types";
import { MediaGallery } from "../common/media-gallery";
import { getMediaURL } from "../../utils/media-utils";
import { getOrganizationOpenDateOutput, isTodayOrFuture } from "../../utils/date-utils";
import { useOrganizationById } from "../../hooks/use-organization.hook";
import { OpenDaysDTO } from "../../types/open-days-dto";

export function OrganizationCard({
    optimizedOrganization,
    onClose,
}: {
    optimizedOrganization: EducationalOrganizationsOptimizedDTO;
    onClose?: () => void;
}) {
    const { data: organization, isPending, isError, refetch } = useOrganizationById(optimizedOrganization.id);

    const [specializationListIsOpen, setSpecializationListIsOpen] = useState(false);
    const [selectedSpecialization, setSpecialization] = useState<ProfessionDTO | null>(null);
    const [openDaysListIsOpen, setOpenDaysListIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'desc' | 'places'>("places");

    useEffect(() => {
        setSpecializationListIsOpen(false);
        setOpenDaysListIsOpen(false);
        setSpecialization(null);
        setActiveTab("places");
    }, [organization]);

    const futureOpenDays = useMemo(
        () => organization?.openDays.filter((od: OpenDaysDTO) => isTodayOrFuture(od.date)), 
        [organization, organization?.openDays]
    );

    const goBack = () => {
        if(specializationListIsOpen) {
            setSpecializationListIsOpen(false);
        }

        if(selectedSpecialization) {
            setSpecialization(null);
            setSpecializationListIsOpen(true);
        }
    };

    let websiteDomain: string | null = null;

    if(organization?.website) {
        let domain = organization.website.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');

        websiteDomain = domain;
    }

    return (
        <div className={`card popup-animated ${isError ? 'error-modal' : ''}`}>
            <div className={`card__accordion ${!specializationListIsOpen && !selectedSpecialization ? "card__accordion--main-info" : ""}`}>
                {/*Header*/}
                <div className="card-top">
                    <div className="card-top__left">
                        <button
                            type="button"
                            className="card-close__btn"
                            onClick={goBack}
                        >
                            <ion-icon name="chevron-back" />
                        </button>

                        <div className="card-top__text-box">
                            <span className={`card-top__another-title ${!selectedSpecialization ? "hide" : ""}`}>
                                {selectedSpecialization?.name}
                            </span>
                            <span className={`card-top__another-title ${selectedSpecialization ? "hide" : ""}`}>
                                Специальности
                            </span>

                            <span className="card-top__org-title card-top__another-title--subtitle">
                                {optimizedOrganization.shortName}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="card-close__btn"
                        onClick={onClose}
                    >
                        <ion-icon name="close" />
                    </button>
                </div>

                {
                    // #region MAIN INFO
                }
                {!isError ? (
                    <>
                    
                        {!selectedSpecialization && !specializationListIsOpen && (
                            (isPending || !organization) ? (
                                <>
                                    <div className="card-middle grid">
                                        <div className="icon-card skeleton-block">
                                            <span>Loading...</span>
                                        </div>
                                        <div className="icon-card skeleton-block">
                                            <span>Loading...</span>
                                        </div>
                                    </div>
                                    <div className="card-middle">
                                        <MediaGallery media={[]} isSkeleton={true} />
                                    </div>
                                    <div className="card-bottom without-padding">
                                        <div className="card-info skeleton-block">
                                            <span>Адрес</span>
                                            <span>Адрес</span>
                                        </div>
                                        <div className="card-info skeleton-block">
                                            <span>Dude</span>
                                        </div>
                                        <div className="card-info skeleton-block">
                                            <span>Dude</span>
                                            <span>Dude</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Contacts */}
                                    <div className="card-middle grid">
                                        {organization.phone && (
                                            <a
                                                href={`tel:${organization.phone}`}
                                                className="icon-card"
                                            >
                                                <ion-icon name="call" />
                                                {organization.phone}
                                            </a>
                                        )}
        
                                        {organization.website && (
                                            <a
                                                href={organization.website}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="icon-card"
                                            >
                                                <ion-icon name="globe-outline" />
                                                <span className="icon-card--text">{websiteDomain}</span>
                                            </a>
                                        )}
                                    </div>
        
                                    {/* Gallery */}
                                    {organization.media.length > 0 && (
                                        <div className="card-middle">
                                            <MediaGallery media={organization.media.map((om: MediaDTO) => ({
                                                type: om.type,
                                                url: getMediaURL(om.objectKey)
                                            }))}/>
                                        </div>
                                    )}
        
                                    {/* Info */}
                                    <div className="card-bottom">
                                        {organization.address && (
                                            <div className="card-info info-separator">
                                                <span>Адрес</span>
                                                <span>{organization.address}</span>
                                            </div>
                                        )}
        
                                        {organization.professionsAndSpecializations.length > 0 && (
                                            <div className="card-info row info-separator">
                                                <div className="card-info">
                                                    <span>Направления подготовки</span>
        
                                                    <div className="card-info__bottom">
                                                        <span className="card-info--black">
                                                            {organization.professionsAndSpecializations.length} направлений
                                                        </span>
        
                                                        <button
                                                            type="button"
                                                            className="card-info--with-btn"
                                                            onClick={() => setSpecializationListIsOpen(true)}
                                                        >
                                                            <span>Посмотреть</span>
                                                            <ion-icon name="chevron-forward" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
        
                                        {organization.openDays.length > 0 && (
                                            <div className="card-info info-separator">
                                                <button
                                                    type="button"
                                                    className="card-info--with-btn"
                                                    onClick={() => setOpenDaysListIsOpen(!openDaysListIsOpen)}
                                                >
                                                    <div className="card-info__bottom">
                                                        <span>День открытых дверей</span>
                                                        <ion-icon className={`card__accordion--dropdown__chevron ${openDaysListIsOpen ? 'open' : ''}`} name="chevron-forward" />
                                                    </div>
                                                </button>
        
                                                <div className={`card__accordion--dropdown  ${openDaysListIsOpen ? "" : "hidden"}`}>
                                                    {futureOpenDays!.map((d: OpenDaysDTO, i: number) => (
                                                        <div key={i}>
                                                            {getOrganizationOpenDateOutput(d)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
        
                                        <div className="card-info">
                                            <span>Условия для студентов</span>
                                            {organization.dorms.length > 0 || organization.militaryDepartment ? (
                                                <span>
                                                    {[
                                                        organization.dorms.length > 0 && 'Общежитие',
                                                        organization.militaryDepartment && (organization.dorms.length > 0 ? 'военная кафедра' : 'Военная кафедра')
                                                    ].filter(Boolean).join(', ')}
                                                </span>
                                            ) : 
                                                <span>Нет</span>
                                            }
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                        {
                            // #endregion
                        }
        
                        {
                            // #region SPECIALIZATIONS
                        }
                        {(specializationListIsOpen && organization) && (
                            <ul className="card__profs-list">
                                {organization.professionsAndSpecializations.map((pos: ProfessionDTO) => (
                                    <li key={pos.id} className="card__list-item">
                                        <button
                                            className="list-item__button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setSpecialization(pos);
                                                setSpecializationListIsOpen(false);
                                            }}
                                        >
                                            <span className="list-item__title">{pos.name}</span>
                                            {(pos.description || pos.placesOfWork.length > 0) && (
                                                <ion-icon name="chevron-forward"></ion-icon>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {
                            // #endregion
                        }
        
                        {
                            // #region SPECIALIZATIONS
                        }
                        {selectedSpecialization && (
                            <>
                                <div className="tab-picker">
                                    <div
                                        className={`tab ${activeTab === "places" ? "active" : ""}`}
                                        onClick={() => setActiveTab("places")}
                                    >
                                        <span>Места работы</span>
                                    </div>
                                    <div
                                        className={`tab ${activeTab === "desc" ? "active" : ""}`}
                                        onClick={() => setActiveTab("desc")}
                                    >
                                        <span>Описание</span>
                                    </div>
                                </div>
                                {activeTab === "places" ? (
                                    selectedSpecialization.placesOfWork.length > 0 ? (
                                        <ul className="card__list links">
                                            {selectedSpecialization.placesOfWork.map((pos, i) => (
                                                <li key={i} className="card__list-item">
                                                    <a 
                                                        className="list-item__button"
                                                        href={`https://trudvsem.ru/vacancy/search?_title=${pos.name}&_regionIds=5500000000000`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <ion-icon name="link-outline" color="var(--blue)"></ion-icon>
                                                        <span className="list-item__title">{pos.name}</span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="tab--no-info">
                                            <span>Информация отсутствует</span>
                                        </div>
                                    )
                                ) : (
                                    selectedSpecialization.description ? (
                                        <div className="card__specialization-desc custom-scroll">
                                            {selectedSpecialization.description}
                                        </div>
                                    ) : (
                                        <div className="tab--no-info">
                                            <span>Информация отсутствует</span>
                                        </div>
                                    )
                                )}
                            </>
                        )}
                        {
                            // #endregion
                        }
                    </>
                ) : (
                    <div className="loader-content">
                        <div className="error-icon">!</div>
                        <div className="loader-text">Не удалось загрузить компанию</div>

                        {!isPending && (
                            <button className="error-button" onClick={() => refetch()}>
                                Повторить
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}